import React, { useMemo } from 'react'
import { ToolkitPageLayout } from '../../../components'
import { Link, useLocation } from 'react-router-dom'

export const CulturalCompetencyTrainingPage: React.FC = () => {
  const location = useLocation()

  // Determine breadcrumbs based on the referring page
  const breadcrumbs = useMemo(() => {
    const baseCrumbs = [{ label: 'Toolkit', path: '/toolkit' }]

    // Check if coming from providers, clinics, or elsewhere
    if (location.state?.from === 'providers') {
      baseCrumbs.push(
        { label: 'Providers', path: '/toolkit/providers' },
        { label: 'Training Modules', path: '/toolkit/providers/training' }
      )
    } else if (location.state?.from === 'clinics') {
      baseCrumbs.push(
        { label: 'Clinics', path: '/toolkit/clinics' },
        { label: 'Training Modules', path: '/toolkit/clinics/training' }
      )
    }

    baseCrumbs.push({
      label: 'Cultural Competency Training',
      path: '/toolkit/shared/cultural-competency-training'
    })

    return baseCrumbs
  }, [location.state])

  return (
    <ToolkitPageLayout
      title="Cultural Competency Training"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-8">
        {/* Quote Section */}
        <div className="bg-gray-50 p-6 border-l-4 border-[#A6410A]">
          <blockquote className="text-lg italic text-gray-700">
            The goal for you is to understand how individual bias affects care
            in medicine.
          </blockquote>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Step 1 & 2: Harvard IAT */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-[#3C2416]">
              Take the Implicit Association Tests
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Register on the following site</li>
              <li>Take at least 2 Implicit Association Tests (IAT)</li>
            </ol>
            <div className="mt-4">
              <a
                href="https://implicit.harvard.edu/implicit/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#A6410A] text-white rounded hover:bg-[#894d35] transition-colors"
              >
                Go to Harvard IAT
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Step 3: AMA Video */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-[#3C2416]">
              Review Educational Materials
            </h3>
            <p className="text-gray-700 mb-4">
              3. Review <em>the American Medical Association</em> video on the
              benefit of Diversity & Inclusion and interventions to remedy
              unconscious bias in medicine
            </p>
            {/* YouTube Video Embed */}
            <div className="aspect-w-16 aspect-h-9 mb-4">
              <iframe
                src="https://www.youtube.com/embed/eadpfj3Br4c"
                title="Exploring Unconscious Bias in Academic Medicine"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-96 rounded-lg"
              />
            </div>
          </div>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Completion Link */}
        <div className="text-center py-8">
          <Link
            to={
              location.state?.from === 'providers'
                ? '/toolkit/providers/training'
                : location.state?.from === 'clinics'
                  ? '/toolkit/clinics/training'
                  : '/toolkit'
            }
            className="inline-block px-8 py-4 bg-[#A6410A] text-white font-semibold rounded hover:bg-[#894d35] transition-colors shadow-md hover:shadow-lg"
          >
            {location.state?.from === 'providers'
              ? 'ALL DONE? CLICK HERE TO RETURN TO THE PROVIDERS SCREEN'
              : location.state?.from === 'clinics'
                ? 'ALL DONE? CLICK HERE TO RETURN TO THE CLINICS SCREEN'
                : 'ALL DONE? CLICK HERE TO RETURN TO THE MAIN SCREEN'}
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
